import {
    ClaimWorkflowConfiguration,
    ClaimWorkflowConfigurationOption,
    ClaimWorkflowConfigurationOptions,
} from '../../api';
import { AUTOMATION_WORKER_DEFINITIONS } from './automation/worker-automation.constants';
import { canEditWorkflowOption } from './worker-configuration.permissions';
import { WorkerConfigurationApiMode } from './worker-configuration.api';
import { stableStringify } from './stable-stringify';

/** A single workflow configuration option value. */
export type WorkflowConfigValue = boolean | number | string | string[];

/** True when a configuration value is unset (missing from config or explicitly cleared). */
export function isUnsetConfigValue(value: WorkflowConfigValue | null | undefined): boolean {
    return value === null || value === undefined;
}

/**
 * Normalizes a numeric config value to a digit-only string.
 * Used so defaults, editor edits, and API numbers share one payload shape.
 */
export function toNumericConfigString(value: unknown): string | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
        const digits = String(Math.trunc(value)).replace(/\D/g, '');
        return digits === '' ? undefined : digits;
    }

    if (typeof value === 'string') {
        const digits = value.replace(/\D/g, '');
        return digits === '' ? undefined : digits;
    }

    return undefined;
}

/** Configured values for one workflow status. */
export type WorkflowStatusConfig = Record<string, WorkflowConfigValue>;

/** Full workflow config keyed by status. */
export type WorkflowConfigMap = Record<string, WorkflowStatusConfig>;

/**
 * Claim workflow configuration with accurate value types.
 * The generated API model types all config values as boolean only.
 */
export interface WorkerWorkflowConfiguration {
    createDate?: string;
    active?: boolean;
    config?: WorkflowConfigMap;
    /** Set by the backend from the session token; do not send on create. */
    createdBy?: string;
}

const AUTOMATION_WORKER_CONFIG_KEYS = new Set(
    Object.values(AUTOMATION_WORKER_DEFINITIONS).map(definition => definition.key),
);

/** Deep-clones a workflow config map. */
export function cloneWorkflowConfigMap(config?: WorkflowConfigMap): WorkflowConfigMap {
    return JSON.parse(JSON.stringify(config ?? {})) as WorkflowConfigMap;
}

/** Returns a new config map with one option set or cleared (removed when unset). */
export function setWorkflowConfigValue(
    config: WorkflowConfigMap,
    status: string,
    key: string,
    value: WorkflowConfigValue | null | undefined,
): WorkflowConfigMap {
    const next = cloneWorkflowConfigMap(config);

    if (!next[status]) {
        next[status] = {};
    }

    if (value === null || value === undefined) {
        delete next[status][key];
        if (Object.keys(next[status]).length === 0) {
            delete next[status];
        }
        return next;
    }

    next[status][key] = value;
    return next;
}

/** True when two workflow config maps are deeply equal. */
export function workflowConfigsEqual(left: WorkflowConfigMap, right: WorkflowConfigMap): boolean {
    return (
        stableStringify(normalizeWorkflowConfigMapForCompare(left)) ===
        stableStringify(normalizeWorkflowConfigMapForCompare(right))
    );
}

/** True when a status has at least one configured option value. */
export function statusHasConfiguredOptions(statusConfig?: WorkflowStatusConfig): boolean {
    return Boolean(statusConfig && Object.keys(statusConfig).length > 0);
}

function normalizeWorkflowConfigValueForCompare(value: WorkflowConfigValue): WorkflowConfigValue {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return toNumericConfigString(value) ?? value;
    }

    return value;
}

function normalizeWorkflowConfigMapForCompare(config: WorkflowConfigMap): WorkflowConfigMap {
    const next: WorkflowConfigMap = {};

    for (const [status, statusConfig] of Object.entries(config)) {
        const normalizedStatus: WorkflowStatusConfig = {};
        for (const [key, value] of Object.entries(statusConfig)) {
            normalizedStatus[key] = normalizeWorkflowConfigValueForCompare(value);
        }
        next[status] = normalizedStatus;
    }

    return next;
}

/**
 * Parses the option metadata `default` into a stored config value.
 * Returns undefined when the options endpoint did not provide a default.
 * Integer/number defaults are stored as digit-only strings (same as the numeric editor).
 */
export function parseOptionDefaultValue(
    option: Pick<ClaimWorkflowConfigurationOption, 'key' | '_default' | 'valueType'>,
): WorkflowConfigValue | undefined {
    const raw = option._default as unknown;
    if (raw === undefined || raw === null) {
        return undefined;
    }

    const valueType = option.valueType ?? '';

    if (valueType === 'boolean') {
        if (raw === true || raw === 'true') {
            return true;
        }

        if (raw === false || raw === 'false') {
            return false;
        }

        return undefined;
    }

    if (valueType === 'integer' || valueType === 'number') {
        return toNumericConfigString(raw);
    }

    if (typeof raw === 'boolean' || typeof raw === 'number' || typeof raw === 'string') {
        return raw;
    }

    return undefined;
}

/**
 * Fills unset option keys from the options-endpoint defaults.
 * Already configured values are left unchanged.
 * Automation worker keys (`PROVIDER_*_WORKER`) are skipped — they are owned by the automation UI.
 * Support-only options are skipped in provider mode so providers never seed or write them.
 */
export function applyWorkflowOptionDefaults(
    config: WorkflowConfigMap,
    optionsList: ClaimWorkflowConfigurationOptions[],
    apiMode: WorkerConfigurationApiMode = 'support',
): WorkflowConfigMap {
    let next = cloneWorkflowConfigMap(config);

    for (const statusOptions of optionsList) {
        const status = statusOptions.workflowStatus?.trim();
        if (!status || AUTOMATION_WORKER_CONFIG_KEYS.has(status)) {
            continue;
        }

        for (const option of statusOptions.options ?? []) {
            const key = option.key?.trim();
            if (!key || option.valueType === 'list') {
                continue;
            }

            if (!canEditWorkflowOption(apiMode, status, key)) {
                continue;
            }

            if (!isUnsetConfigValue(next[status]?.[key])) {
                continue;
            }

            const defaultValue = parseOptionDefaultValue(option);
            if (defaultValue === undefined) {
                continue;
            }

            next = setWorkflowConfigValue(next, status, key, defaultValue);
        }
    }

    return next;
}

/** Maps the API response to the locally accurate configuration shape. */
export function parseWorkerWorkflowConfiguration(
    config: ClaimWorkflowConfiguration,
): WorkerWorkflowConfiguration {
    return {
        createDate: config.createDate,
        active: config.active,
        createdBy: config.createdBy,
        config: config.config as unknown as Record<string, WorkflowStatusConfig> | undefined,
    };
}
